// ─────────────────────────────────────────────────────────────────────────
// defaults.ts — the national default assumption set + attribution register.
//
// SINGLE SOURCE OF TRUTH for the volatile, centrally-maintained figures. A
// client overrides only what genuinely differs (their retailer FiT, brand,
// copy, theme); everything else inherits these, so a regulatory refresh here
// propagates to every consumer that hasn't overridden the value.
//
// Every time-sensitive figure is a Sourced<T> carrying its licence attribution
// and review date. The refresh checklist lives in MAINTENANCE.md.
// Review cadence (plan §D): Jan 1 (STC deeming step-down), Jul 1 (FiT, retail
// rates, emissions), one-off 2026-05-01 (battery taper). See MAINTENANCE.md.
// ─────────────────────────────────────────────────────────────────────────

import type { LabelledSourced } from '../core/freshness.js';
import type { Sourced } from '../core/types.js';
import type { ClientConfig, RegionRates, SolarCalcConfig } from './schema.js';

// Attribution strings (licence obligation — exact wording, brief v1 §6).
const ATTR = {
  cer: 'Based on Clean Energy Regulator material licensed under a Creative Commons Attribution 4.0 licence.',
  gnaf:
    'Incorporates or developed using G-NAF © Geoscape Australia licensed by the Commonwealth of Australia under the Open Geo-coded National Address File (G-NAF) End User Licence Agreement.',
  aer: 'Includes material from the Australian Energy Regulator under CC BY 4.0.',
  esc: 'Includes material from the Essential Services Commission (Victoria) under CC BY 4.0.',
  ipart:
    'Includes feed-in tariff benchmarks from IPART and state regulators under CC BY 4.0.',
} as const;

const src = <T>(value: T, s: Omit<Sourced<T>, 'value'>): Sourced<T> => ({
  value,
  ...s,
});

// ── retail rate / FiT / supply charge by distributor region ──────────────────
// Indicative AU 2025–26 defaults (confidence 'estimate'); refresh each Jul 1
// against AER DMO / Vic VDO (retail) and IPART + state regulators (FiT).
function regionRates(
  retail: number,
  fit: number,
  supply: number,
  retailSource: string,
  retailUrl: string,
  retailAttr: string,
): RegionRates {
  const common = {
    license: 'CC BY 4.0',
    lastUpdated: '2025-07-01',
    reviewDue: '2026-07-01',
    confidence: 'estimate' as const,
  };
  return {
    retailRate: src(retail, {
      source: retailSource,
      url: retailUrl,
      attribution: retailAttr,
      ...common,
    }),
    feedInTariff: src(fit, {
      source: 'IPART / state regulator feed-in benchmark',
      url: 'https://www.ipart.nsw.gov.au/',
      attribution: ATTR.ipart,
      ...common,
    }),
    supplyChargePerDay: src(supply, {
      source: retailSource,
      url: retailUrl,
      attribution: retailAttr,
      ...common,
    }),
  };
}

const AER = {
  source: 'AER Default Market Offer 2026-27 (DMO 8) — Cost assessment model',
  url: 'https://www.aer.gov.au/industry/registers/resources/reviews/default-market-offer-2026-27/final-determination',
  attr: ATTR.aer,
};
const ESC = {
  source: 'Essential Services Commission — Victorian Default Offer',
  url: 'https://www.esc.vic.gov.au/electricity-and-gas/prices-tariffs-and-benchmarks/victorian-default-offer',
  attr: ATTR.esc,
};
const IPART_FIT_URL =
  'https://www.ipart.nsw.gov.au/review/energy/solar-feed-tariff-benchmarks-2024-25-2026-27';
const VIC_FIT_URL =
  'https://www.esc.vic.gov.au/electricity-and-gas/prices-tariffs-and-benchmarks/minimum-feed-tariff';

// ── single-figure builders, by confidence ───────────────────────────────────
// verified = a human checked this against the current authority on `lastUpdated`
// (the value may be a representative figure derived from that authority by a
// documented method — see each `source` string). estimate = a reasonable default
// not freshly pulled, which the freshness gate surfaces as a warning.
const verified = (
  value: number,
  source: string,
  url: string,
  attribution: string,
  reviewDue: string,
): Sourced<number> =>
  src(value, { source, url, attribution, license: 'CC BY 4.0', lastUpdated: '2026-06-02', reviewDue, confidence: 'verified' });

const estimate = (
  value: number,
  source: string,
  url: string,
  attribution: string,
  reviewDue = '2026-07-01',
): Sourced<number> =>
  src(value, { source, url, attribution, license: 'CC BY 4.0', lastUpdated: '2025-07-01', reviewDue, confidence: 'estimate' });

export const defaultConfig: SolarCalcConfig = {
  version: '2026.06.5',
  dataUrl: 'https://solar-calc.sellmyservice.com.au/postcodeZones.json',
  client: {
    clientName: 'Your Solar Co',
    phone: '',
    quoteUrl: '#',
    webhookUrl: '', // empty = demo
  },

  audience: 'residential',
  modes: {
    residential: {
      label: 'Residential',
      gstRegistered: false,
      // Inherits sizes / cost / shares / copy from `assumptions` + `copy`.
      profiles: [
        { value: 'mostly-daytime', label: 'Mostly during the day' },
        { value: 'mixed', label: 'A mix of day and evening' },
        { value: 'mostly-evenings', label: 'Mostly evenings' },
        { value: 'pool-or-ev', label: 'Heavy use (pool / EV)' },
      ],
    },
    commercial: {
      label: 'Commercial',
      gstRegistered: true, // claims input tax credits → ex-GST economics
      systemSizesKw: [10, 30, 50, 100],
      sizeSliderMin: 10,
      sizeSliderMax: 100,
      sizeSliderStep: 1,
      defaultSizeKw: 30,
      costPerKw: 1050, // cheaper per kW at commercial scale
      // For the DMO regions (NSW/SE-QLD/SA) the element uses the exact AER
      // small-business usage charge + supply (regions[*].commercialRetailRate).
      // This factor is only the FALLBACK for regions without an explicit small-
      // business figure — AER data shows the SB usage charge ≈ residential (the
      // real commercial difference is the much higher supply charge + demand
      // charges, the latter still not modelled — see `note`).
      retailRateFactor: 1.0,
      defaultBillPeriod: 'monthly',
      // Businesses use most power during the day → higher self-consumption.
      daytimeLoadShareByProfile: {
        'mostly-daytime': 0.85,
        mixed: 0.75,
        'mostly-evenings': 0.55,
        'pool-or-ev': 0.8,
      },
      title: 'Commercial solar payback calculator',
      intro: 'Estimate payback for your business premises — indicative only, not a quote.',
      billLabel: 'Electricity spend',
      billHint: 'Enter your typical quarterly or monthly electricity spend, or drag the slider.',
      billDefault: 2000,
      billMax: 15000,
      billStep: 100,
      profileLabel: 'When does the business mostly use power?',
      profiles: [
        { value: 'mostly-daytime', label: 'Business hours (8am–5pm)' },
        { value: 'mixed', label: 'Extended hours (6am–10pm)' },
        { value: 'pool-or-ev', label: '24/7 operations' },
      ],
      costLabel: 'Solar cost ($/kW installed)',
      quoteHeading: 'Get a site assessment for your business',
      note: 'Commercial estimates exclude demand-charge (kVA) savings, large-scale generation certificates (LGCs) for systems over 100 kW, and commercial battery incentives — a site assessment covers these.',
    },
  },

  stcPrice: src(38, {
    source: 'STC clearing-house / market spot price',
    url: 'https://cer.gov.au/markets/small-scale-technology-percentage-and-certificate-prices',
    license: 'CC BY 4.0',
    attribution: ATTR.cer,
    lastUpdated: '2026-04-01',
    reviewDue: '2026-09-01', // quarterly spot-check
    confidence: 'estimate',
  }),

  sresEndYear: src(2030, {
    source: 'CER Small-scale Renewable Energy Scheme (SRES) — deeming wind-down',
    url: 'https://cer.gov.au/schemes/renewable-energy-target/small-scale-renewable-energy-scheme',
    license: 'CC BY 4.0',
    attribution: ATTR.cer,
    lastUpdated: '2026-01-01',
    reviewDue: '2027-01-01', // deeming years step down each Jan 1
    confidence: 'verified',
  }),

  // National (NSW-representative) scope 2 grid factor; state factors vary
  // (VIC ~0.78, QLD ~0.67, SA/TAS much lower). The calc uses one global factor.
  emissionsFactorKgPerKwh: src(0.66, {
    source: 'National Greenhouse Accounts Factors 2024 — scope 2 grid electricity (NSW/national)',
    url: 'https://www.dcceew.gov.au/climate-change/publications/national-greenhouse-accounts-factors-2024',
    license: 'CC BY 4.0',
    attribution: 'Includes material from the National Greenhouse Accounts under CC BY 4.0.',
    lastUpdated: '2026-06-02',
    reviewDue: '2026-09-01', // NGA factors refresh ~annually (Aug)
    confidence: 'verified',
  }),

  battery: {
    // Representative installed price; the market spans ~$440–$1,630/kWh (Solar
    // Choice quarterly index: ~$865/kWh for a 10 kWh system, early 2026).
    costPerKwh: src(900, {
      source: 'Solar Choice quarterly battery price index (AU, ~$865/kWh for 10 kWh)',
      url: 'https://www.solarchoice.net.au/solar-batteries/price/',
      lastUpdated: '2026-06-03',
      reviewDue: '2026-09-01', // quarterly
      confidence: 'estimate',
    }),
    // Cheaper Home Batteries Program: STCs per usable kWh, declining twice a year
    // (1 Jan / 1 Jul) to scheme end in 2030. 6.8/kWh from 1 May 2026 (≈ $252/kWh
    // off, the first 14 kWh) is verified; later years are the published glide path.
    stcDeemingPerKwhByYear: src(
      { '2025': 9.3, '2026': 6.8, '2027': 5.0, '2028': 3.6, '2029': 2.7, '2030': 2.1 },
      {
        source: 'Cheaper Home Batteries Program — STC deeming per usable kWh (DCCEEW/CER)',
        url: 'https://www.dcceew.gov.au/energy/programs/cheaper-home-batteries/small-scale-technology-certificates',
        license: 'CC BY 4.0',
        attribution: ATTR.cer,
        lastUpdated: '2026-06-03',
        reviewDue: '2027-01-01', // next scheduled step-down 1 Jan 2027
        confidence: 'estimate',
      },
    ),
    // Tiered STC factor by usable capacity (from 1 May 2026).
    taperBands: [
      { upToKwh: 14, factor: 1.0 },
      { upToKwh: 28, factor: 0.6 },
      { upToKwh: 50, factor: 0.15 },
    ],
  },

  // Retail rates for the DMO/VDO regions are DERIVED from the current regulator
  // determination: (annual reference price ÷ model annual usage) − supply charge.
  // The reference price is the verified authority figure; the c/kWh split is a
  // documented model (the regulators don't publish a single c/kWh). Supply charges
  // are representative network values (estimate). Refresh each 1 July — see MAINTENANCE.md.
  regions: {
    NSW: {
      retailRate: verified(0.331, 'AER DMO 2026-27 (DMO 8), Ausgrid residential flat usage charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      feedInTariff: verified(0.06, 'IPART NSW all-day solar feed-in benchmark 2025-26 (midpoint of 4.8–7.3 c/kWh)', IPART_FIT_URL, ATTR.ipart, '2026-07-01'),
      supplyChargePerDay: verified(1.66, 'AER DMO 2026-27, Ausgrid residential supply charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      commercialRetailRate: verified(0.316, 'AER DMO 2026-27, Ausgrid small-business flat usage charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      commercialSupplyChargePerDay: verified(3.73, 'AER DMO 2026-27, Ausgrid small-business supply charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
    },
    ACT: regionRates(0.27, 0.06, 1.0, 'ICRC (ACT) regulated retail (representative)', 'https://www.icrc.act.gov.au/', ATTR.aer),
    VIC: {
      retailRate: verified(0.26, 'ESC VDO 2026-27 Final Decision, CitiPower domestic: $1,481/yr ÷ 4,000 kWh, less ~$1.20/day supply (incl GST)', ESC.url, ATTR.esc, '2027-07-01'),
      // Victoria abolished the mandatory minimum FiT on 1 Jul 2025 — now market-set
      // (~0c daytime, 6.5–10c evening). Representative all-day estimate; ESC monitors.
      feedInTariff: estimate(0.033, 'VIC market feed-in tariff (representative; ESC mandatory minimum ended 1 Jul 2025)', VIC_FIT_URL, ATTR.esc),
      supplyChargePerDay: estimate(1.2, 'CitiPower domestic daily supply (representative; ESC VDO 2026-27)', ESC.url, ATTR.esc),
      commercialRetailRate: verified(0.24, 'ESC VDO 2026-27 Final Decision, CitiPower small business: $3,033/yr ÷ 10,000 kWh, less ~$1.70/day supply (incl GST)', ESC.url, ATTR.esc, '2027-07-01'),
      commercialSupplyChargePerDay: estimate(1.7, 'CitiPower small-business daily supply (representative; ESC VDO 2026-27)', ESC.url, ATTR.esc),
    },
    SE_QLD: {
      retailRate: verified(0.28, 'AER DMO 2026-27 (DMO 8), Energex residential flat usage charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      feedInTariff: estimate(0.05, 'SE-QLD market feed-in tariff (representative; SE-QLD has no mandated FiT)', IPART_FIT_URL, ATTR.ipart),
      supplyChargePerDay: verified(1.92, 'AER DMO 2026-27, Energex residential supply charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      commercialRetailRate: verified(0.289, 'AER DMO 2026-27, Energex small-business flat usage charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      commercialSupplyChargePerDay: verified(2.62, 'AER DMO 2026-27, Energex small-business supply charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
    },
    REGIONAL_QLD: {
      retailRate: estimate(0.28, 'Queensland regulated retail (Ergon, representative)', 'https://www.qca.org.au/', ATTR.ipart),
      // Regional QLD (Ergon) FiT is regulated by the QCA — a real published rate.
      feedInTariff: verified(0.0866, 'QCA regional Queensland feed-in tariff 2025-26 (Ergon)', 'https://www.qca.org.au/', ATTR.ipart, '2026-07-01'),
      supplyChargePerDay: estimate(1.2, 'Ergon daily supply charge (representative)', 'https://www.qca.org.au/', ATTR.ipart),
    },
    SA: {
      retailRate: verified(0.419, 'AER DMO 2026-27 (DMO 8), SA Power Networks residential flat usage charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      feedInTariff: estimate(0.04, 'SA market feed-in tariff (representative)', IPART_FIT_URL, ATTR.ipart),
      supplyChargePerDay: verified(1.8, 'AER DMO 2026-27, SA Power Networks residential supply charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      commercialRetailRate: verified(0.448, 'AER DMO 2026-27, SA Power Networks small-business flat usage charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
      commercialSupplyChargePerDay: verified(1.86, 'AER DMO 2026-27, SA Power Networks small-business supply charge (incl GST)', AER.url, ATTR.aer, '2027-07-01'),
    },
    TAS: regionRates(0.3, 0.08, 1.1, 'Tasmanian Economic Regulator (representative)', 'https://www.economicregulator.tas.gov.au/', ATTR.ipart),
    WA: regionRates(0.3, 0.025, 1.1, 'Synergy DEBS (WA, representative)', 'https://www.synergy.net.au/', ATTR.ipart),
    NT: regionRates(0.28, 0.085, 1.2, 'Jacana Energy (NT, representative)', 'https://www.jacanaenergy.com.au/', ATTR.ipart),
  },

  assumptions: {
    // Conservative system efficiency (Solar Choice benchmark ~0.75; we sit a
    // touch above at 0.80). Lower = longer, more honest payback. Disclosed below.
    performanceRatio: 0.8,
    orientationFactor: 1.0, // default north-facing (see orientationOptions)
    shadeFactor: 1.0, // default no shade (see shadeOptions)
    firstYearDegradation: 0.03, // 97% in year 1 (LID)
    degradation: 0.007, // then ~0.7%/yr → ~82% by year 25
    electricityInflation: 0.03,
    fitDecline: 0.05,
    fitFloor: 0.03,
    costPerKw: 1250,
    panelWatts: 440,
    discountRate: 0.05,
    systemSizesKw: [5, 6.6, 8, 10, 13.2],
    // Daytime share of usage by profile (benchmark-aligned; mirrors the pure
    // fallback DEFAULT_DAYTIME_LOAD_SHARES in core/calc.ts — kept inline so this
    // config module has zero runtime imports for the raw-node test/gate runners).
    daytimeLoadShareByProfile: {
      'mostly-daytime': 0.65,
      mixed: 0.45,
      'mostly-evenings': 0.3,
      'pool-or-ev': 0.5,
    },
  },

  defaults: {
    billPeriod: 'quarterly',
    usageProfile: 'mixed',
    sizeKw: 6.6,
    installYear: 2026,
    hasExistingSolar: false,
    battery: {
      defaultUsableKwh: 10,
      roundTripEfficiency: 0.9,
    },
  },

  // Roof orientation production loss vs optimal north. factor = 1 − lossPct/100,
  // so the tooltip figure and the calc factor are the SAME number.
  orientationOptions: [
    { value: 'N', label: 'North', factor: 1.0, lossPct: 0 },
    { value: 'NE_NW', label: 'North-east / north-west', factor: 0.93, lossPct: 7 },
    { value: 'E_W', label: 'East / west', factor: 0.85, lossPct: 15 },
    { value: 'S', label: 'South', factor: 0.62, lossPct: 38 },
  ],
  shadeOptions: [
    { value: 'none', label: 'None', factor: 1.0, lossPct: 0 },
    { value: 'light', label: 'Light', factor: 0.95, lossPct: 5 },
    { value: 'moderate', label: 'Moderate', factor: 0.85, lossPct: 15 },
    { value: 'heavy', label: 'Heavy', factor: 0.7, lossPct: 30 },
  ],

  tooltips: {
    orientation:
      'Roof direction vs true north changes output: North 0% · NE/NW ~7% · E/W ~15% · South ~38% loss. Pitch matters too — roughly 1% loss per 5° away from the optimal tilt. The figure you pick is applied directly to the estimate.',
    shade:
      'Shade from trees, chimneys or neighbouring buildings cuts output: None 0% · Light ~5% · Moderate ~15% · Heavy ~30%. A site assessment measures this properly.',
    selfConsumption:
      'Self-consumption — how much of your solar you use as it is generated — is the single biggest unknown. Using more during the day (or adding a battery) lifts it and shortens payback.',
    roi: 'Return on investment: total savings over 25 years as a percentage of the net upfront cost.',
    irr: 'Internal rate of return: the effective annual return that makes the 25-year cash flow break even — compare it to a savings or investment rate.',
    npv: 'Net present value: lifetime savings discounted to today’s dollars at the disclosed discount rate. Positive = worth more than the upfront cost.',
  },

  // Built below (after the literal) so the displayed strings derive from the
  // actual assumption values — they can't drift from the numbers in use.
  disclosures: [],

  sources: [
    {
      id: 'cer-zone',
      name: 'Clean Energy Regulator — postcode zone ratings + STC deeming',
      used: 'system sizing, STC dollar value, generation factor',
      license: 'CC BY 4.0',
      url: 'https://cer.gov.au/document/postcode-zone-ratings-and-zones-solar-panel-systems',
      attribution: ATTR.cer,
      lastUpdated: '2026-06-02',
    },
    {
      id: 'gnaf',
      name: 'G-NAF © Geoscape Australia (via joelkoen/postcodes-au)',
      used: 'postcode → state / latitude-longitude',
      license: 'Open G-NAF EULA (CC BY 4.0 base)',
      url: 'https://github.com/joelkoen/postcodes-au',
      attribution: ATTR.gnaf,
      lastUpdated: '2026-06-02',
    },
    {
      id: 'aer-dmo',
      name: 'Australian Energy Regulator — Default Market Offer 2026-27 (DMO 8)',
      used: 'default retail rate (NSW / SE-QLD / SA)',
      license: 'CC BY 4.0',
      url: AER.url,
      attribution: ATTR.aer,
      lastUpdated: '2026-06-02',
    },
    {
      id: 'esc-vdo',
      name: 'Essential Services Commission — Victorian Default Offer + minimum FiT',
      used: 'default retail rate + feed-in tariff (VIC)',
      license: 'CC BY 4.0',
      url: ESC.url,
      attribution: ATTR.esc,
      lastUpdated: '2026-06-02',
    },
    {
      id: 'ipart-fit',
      name: 'IPART — NSW solar feed-in tariff benchmark (2024-25 to 2026-27)',
      used: 'feed-in tariff benchmark',
      license: 'CC BY 4.0',
      url: IPART_FIT_URL,
      attribution: ATTR.ipart,
      lastUpdated: '2026-06-02',
    },
  ],

  copy: {
    heading: 'Solar payback calculator',
    intro:
      'See an indicative system size, cost after the STC discount, annual saving and payback for your postcode. It is a starting point, not a quote.',
    resultDisclaimer:
      'Indicative only — not a quote. Your real result depends on your roof, shade, switchboard, tariff and how you use power. A site assessment gives the real number.',
    aboutEstimate:
      'Indicative only — not a quote. Your real result depends on roof orientation, shade, switchboard and export limits, your retailer’s tariff, future electricity prices and how you use power. A site assessment gives the real number.',
    dataSourcesIntro:
      'System sizing and rebate (STC) figures are based on Clean Energy Regulator material licensed under a Creative Commons Attribution 4.0 licence. Location data incorporates or is developed using G-NAF © Geoscape Australia licensed by the Commonwealth of Australia under the Open G-NAF End User Licence Agreement. Default electricity rates and feed-in tariffs are drawn from the Australian Energy Regulator (Default Market Offer), the Essential Services Commission (Victorian Default Offer) and IPART, used under CC BY 4.0. Assumptions current as of {asOf}.',
    ctaLabel: 'Get this quoted properly',
    leadHeading: 'Get a real quote on these numbers',
    demoNotice:
      'This is a demonstration — nothing was sent. On a live build this books a site assessment within one business day.',
    assumptionsHeading: 'Assumptions behind these numbers',
    gstNote:
      'GST: the retail rate is GST-inclusive (so your bill savings include GST); residential feed-in credits are GST-free, so exports are valued without it.',
    selfConsumptionNote:
      'Self-consumption is the biggest unknown in any solar estimate. Using more power during the day — or adding a battery — lifts it and shortens payback.',
    sizingNote:
      'This system generates more than your current usage. 6.6 kW is the smallest most installers quote, and the surplus exports now or feeds a future battery or EV — but a site assessment sizes it to your bill.',
    zeroFitLabel: 'If feed-in = 0c',
    shareLabel: 'Share these numbers',
    shareCopiedLabel: 'Link copied',
    addressLabel: 'Address',
    addressHint: 'Used to prepare your quote — your postcode above sets the solar zone.',
    commercialGstNote:
      'Commercial figures are shown ex-GST, since a GST-registered business claims the GST back on both the system and its power bills.',
  },

  // Empty by default: the neutral token defaults live in styles/tokens.css so a
  // host site can reskin purely with CSS custom properties (the white-label seam).
  // A client that prefers JS-driven theming may pass tokens here; those are then
  // applied inline on the host element (highest priority).
  theme: {},
};

// ── disclosed-assumptions panel (built FROM the values above, so the displayed
// strings can never drift from the numbers actually used). Each row is a
// Sourced<string> carrying its benchmark + lastUpdated. (brief §10a gap E) ──
{
  const a = defaultConfig.assumptions;
  const est = (
    label: string,
    value: string,
    source: string,
    url = 'https://www.solarchoice.net.au/',
  ): (typeof defaultConfig.disclosures)[number] => ({
    label,
    value,
    source,
    url,
    license: 'benchmark estimate',
    lastUpdated: '2026-06-02',
    reviewDue: '2027-06-02',
    confidence: 'estimate',
  });
  const pct = (f: number): string => `${Math.round(f * 100)}%`;
  defaultConfig.disclosures = [
    est('System efficiency (performance ratio)', pct(a.performanceRatio), 'Solar Choice system-efficiency benchmark (~75%); we use a slightly higher 80%'),
    est('Panel degradation', `${pct(1 - a.firstYearDegradation)} year 1 → ~${pct((1 - a.firstYearDegradation) * (1 - a.degradation) ** 24)} year 25`, 'Solar Choice / manufacturer degradation norms'),
    est('Electricity price inflation', `${pct(a.electricityInflation)} / yr`, 'Calculator convention (conservative)'),
    est('Feed-in tariff decline', `${pct(a.fitDecline)} / yr, floor ${(a.fitFloor * 100).toFixed(1)}c`, 'Calculator convention (conservative)'),
    est('Battery cycling', '1 cycle / day', 'Solargain battery-cycling convention'),
    (() => {
      const deeming = defaultConfig.battery.stcDeemingPerKwhByYear.value[String(defaultConfig.defaults.installYear)] ?? 6.8;
      const perKwh = Math.round(deeming * defaultConfig.stcPrice.value);
      return est(
        'Battery rebate (Cheaper Home Batteries)',
        `${deeming} STCs/kWh ≈ $${perKwh}/kWh, tapered 100/60/15% over 14/28/50 kWh`,
        'Cheaper Home Batteries Program (DCCEEW/CER), declining to 2030',
        'https://www.dcceew.gov.au/energy/programs/cheaper-home-batteries',
      );
    })(),
    est('Battery installed cost', `~$${defaultConfig.battery.costPerKwh.value}/kWh`, 'Solar Choice quarterly battery price index'),
    est('Discount rate (for NPV)', `${pct(a.discountRate)} / yr`, 'Calculator convention'),
    est('GST treatment', 'Retail incl. GST; export GST-free', 'Solar Choice (residential GST treatment)'),
  ];
}

// ── deep merge (client overrides national defaults) ──────────────────────────
function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function deepMerge<T>(base: T, override: unknown): T {
  if (!isObject(base) || !isObject(override)) {
    return (override === undefined ? base : (override as T));
  }
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(override)) {
    out[k] = k in base ? deepMerge((base as Record<string, unknown>)[k], v) : v;
  }
  return out as T;
}

/** Resolve a client's partial overrides against the national defaults. */
export function resolveConfig(overrides?: ClientConfig): SolarCalcConfig {
  if (!overrides) return defaultConfig;
  return deepMerge(defaultConfig, overrides);
}

/** Collect every Sourced figure for the freshness audit. */
export function listSourced(config: SolarCalcConfig): LabelledSourced[] {
  const list: LabelledSourced[] = [
    { label: 'STC price', value: config.stcPrice },
    { label: 'SRES end year', value: config.sresEndYear },
    { label: 'Grid emissions factor', value: config.emissionsFactorKgPerKwh },
    { label: 'Battery installed cost', value: config.battery.costPerKwh },
    { label: 'Battery STC deeming', value: config.battery.stcDeemingPerKwhByYear },
  ];
  for (const [region, rates] of Object.entries(config.regions)) {
    list.push({ label: `${region} retail rate`, value: rates.retailRate });
    list.push({ label: `${region} feed-in tariff`, value: rates.feedInTariff });
    list.push({ label: `${region} supply charge`, value: rates.supplyChargePerDay });
  }
  return list;
}
